import { z, ZodIssueCode } from 'zod';

// If more than one version is supported, use z.union
const SchemaVersions = z.literal('2023-09-28');
// Keep deprecated versions here just for reference
z.union([
    z.literal('2023-08-23'),
    z.literal('2023-07-26'),
    z.literal('2023-06-27'),
]);

const DefinitionPropertyTypeSchema = z.enum([
    'Text',
    'RichText',
    'Number',
    'Date',
    'Boolean',
    'Location',
    'Media',
    'Object',
    'Hyperlink',
    'Array',
    'Link',
]);
const DefinitionPropertyKeySchema = z
    .string()
    .regex(/^[a-zA-Z0-9-_]{1,32}$/, { message: 'Property needs to match: /^[a-zA-Z0-9-_]{1,32}$/' });
const PrimitiveValueSchema = z.union([
    z.string(),
    z.boolean(),
    z.number(),
    z.record(z.any(), z.any()),
    z.undefined(),
]);
const uuidKeySchema = z
    .string()
    .regex(/^[a-zA-Z0-9-_]{1,21}$/, { message: 'Does not match /^[a-zA-Z0-9-_]{1,21}$/' });
/**
 * Property keys for imported components have a limit of 32 characters (to be implemented) while
 * property keys for patterns have a limit of 54 characters (<32-char-variabl-name>_<21-char-nanoid-id>).
 * Because we cannot distinguish between the two in the componentTree, we will use the larger limit for both.
 */
const propertyKeySchema = z
    .string()
    .regex(/^[a-zA-Z0-9-_]{1,54}$/, { message: 'Does not match /^[a-zA-Z0-9-_]{1,54}$/' });
const DataSourceSchema = z.record(uuidKeySchema, z.object({
    sys: z.object({
        type: z.literal('Link'),
        id: z.string(),
        linkType: z.enum(['Entry', 'Asset']),
    }),
}));
const ValuesByBreakpointSchema = z.record(z.lazy(() => PrimitiveValueSchema));
const DesignValueSchema = z
    .object({
    type: z.literal('DesignValue'),
    valuesByBreakpoint: ValuesByBreakpointSchema,
})
    .strict();
const BoundValueSchema = z
    .object({
    type: z.literal('BoundValue'),
    path: z.string(),
})
    .strict();
const HyperlinkValueSchema = z
    .object({
    type: z.literal('HyperlinkValue'),
    linkTargetKey: z.string(),
    /** Allows to override parts of the URL, e.g. the locale */
    overrides: z.object({}).optional(),
})
    .strict();
const UnboundValueSchema = z
    .object({
    type: z.literal('UnboundValue'),
    key: z.string(),
})
    .strict();
const ComponentValueSchema = z
    .object({
    type: z.literal('ComponentValue'),
    key: z.string(),
})
    .strict();
// TODO: finalize schema structure before release
// https://contentful.atlassian.net/browse/LUMOS-523
const NoValueSchema = z.object({ type: z.literal('NoValue') }).strict();
const ComponentPropertyValueSchema = z.discriminatedUnion('type', [
    DesignValueSchema,
    BoundValueSchema,
    UnboundValueSchema,
    HyperlinkValueSchema,
    ComponentValueSchema,
    NoValueSchema,
]);
// TODO: finalize schema structure before release
// https://contentful.atlassian.net/browse/LUMOS-523
const VariableMappingSchema = z.object({
    patternPropertyDefinitionId: propertyKeySchema,
    type: z.literal('ContentTypeMapping'),
    pathsByContentType: z.record(z.string(), z.object({ path: z.string() })),
});
const VariableMappingsSchema = z.record(propertyKeySchema, VariableMappingSchema);
// TODO: finalize schema structure before release
// https://contentful.atlassian.net/browse/LUMOS-523
const PatternPropertyDefinitionSchema = z.object({
    defaultValue: z
        .record(z.string(), z.object({
        sys: z.object({
            type: z.literal('Link'),
            id: z.string(),
            linkType: z.enum(['Entry']),
        }),
    }))
        .optional(),
    contentTypes: z.record(z.string(), z.object({
        sys: z.object({
            type: z.literal('Link'),
            id: z.string(),
            linkType: z.enum(['ContentType']),
        }),
    })),
});
const PatternPropertyDefinitionsSchema = z.record(propertyKeySchema, PatternPropertyDefinitionSchema);
// TODO: finalize schema structure before release
// https://contentful.atlassian.net/browse/LUMOS-523
const PatternPropertySchema = z.object({
    type: z.literal('BoundValue'),
    path: z.string(),
    contentType: z.string(),
});
const PatternPropertysSchema = z.record(propertyKeySchema, PatternPropertySchema);
const BreakpointSchema = z
    .object({
    id: propertyKeySchema,
    query: z.string().regex(/^\*$|^<[0-9*]+px$/),
    previewSize: z.string(),
    displayName: z.string(),
    displayIcon: z.enum(['desktop', 'tablet', 'mobile']).optional(),
})
    .strict();
const UnboundValuesSchema = z.record(uuidKeySchema, z.object({
    value: PrimitiveValueSchema,
}));
const ComponentTreeNodeIdSchema = z
    .string()
    .regex(/^[a-zA-Z0-9]{1,8}$/, { message: 'Does not match /^[a-zA-Z0-9]{1,8}$/' });
// Use helper schema to define a recursive schema with its type correctly below
const BaseComponentTreeNodeSchema = z.object({
    id: ComponentTreeNodeIdSchema.optional(),
    definitionId: DefinitionPropertyKeySchema,
    displayName: z.string().optional(),
    slotId: z.string().optional(),
    variables: z.record(propertyKeySchema, ComponentPropertyValueSchema),
    patternProperties: PatternPropertysSchema.optional(),
});
const ComponentTreeNodeSchema = BaseComponentTreeNodeSchema.extend({
    children: z.lazy(() => ComponentTreeNodeSchema.array()),
});
const BindingSourceTypeEnumSchema = z
    .array(z.enum(['entry', 'asset', 'manual', 'experience']))
    .nonempty();
const ComponentVariableSchema = z.object({
    displayName: z.string().optional(),
    type: DefinitionPropertyTypeSchema,
    description: z.string().optional(),
    group: z.string().optional(),
    defaultValue: PrimitiveValueSchema.or(ComponentPropertyValueSchema).optional(),
    validations: z
        .object({
        bindingSourceType: BindingSourceTypeEnumSchema.optional(),
        required: z.boolean().optional(),
        format: z.literal('URL').optional(),
        in: z
            .array(z.object({
            value: z.union([z.string(), z.number()]),
            displayName: z.string().optional(),
        }))
            .optional(),
    })
        .optional(),
});
const ComponentVariablesSchema = z.record(z.string().regex(/^[a-zA-Z0-9-_]{1,54}$/), // Here the key is <variableName>_<nanoidId> so we need to allow for a longer length
ComponentVariableSchema);
const THUMBNAIL_IDS = [
    'columns',
    'columnsPlusRight',
    'imagesSquare',
    'subtitles',
    'rowsPlusBottom',
    'userRectangle',
    'textbox',
    'monitorPlay',
    'article',
    'table',
    'star',
    'heartStraight',
    'frameCorners',
    'rows',
    'dotsThreeOutline',
    'listDashes',
    'checkerBoard',
    'gridFour',
    'slideshow',
    'diamondsFour',
    'cards',
    'textColumns',
    'duplex',
];
const ComponentSettingsSchema = z.object({
    variableDefinitions: ComponentVariablesSchema,
    thumbnailId: z.enum(THUMBNAIL_IDS).optional(),
    category: z.string().max(50, 'Category must contain at most 50 characters').optional(),
    variableMappings: VariableMappingsSchema.optional(),
    patternPropertyDefinitions: PatternPropertyDefinitionsSchema.optional(),
});
const UsedComponentsSchema = z.array(z.object({
    sys: z.object({
        type: z.literal('Link'),
        id: z.string(),
        linkType: z.literal('Entry'),
    }),
}));
const breakpointsRefinement = (value, ctx) => {
    if (!value.length || value[0].query !== '*') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `The first breakpoint should include the following attributes: { "query": "*" }`,
        });
    }
    const hasDuplicateIds = value.some((currentBreakpoint, currentBreakpointIndex) => {
        // check if the current breakpoint id is found in the rest of the array
        const breakpointIndex = value.findIndex((breakpoint) => breakpoint.id === currentBreakpoint.id);
        return breakpointIndex !== currentBreakpointIndex;
    });
    if (hasDuplicateIds) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Breakpoint IDs must be unique`,
        });
    }
    // Extract the queries boundary by removing the special characters around it
    const queries = value.map((bp) => bp.query === '*' ? bp.query : parseInt(bp.query.replace(/px|<|>/, '')));
    // sort updates queries array in place so we need to create a copy
    const originalQueries = [...queries];
    queries.sort((q1, q2) => {
        if (q1 === '*') {
            return -1;
        }
        if (q2 === '*') {
            return 1;
        }
        return q1 > q2 ? -1 : 1;
    });
    if (originalQueries.join('') !== queries.join('')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Breakpoints should be ordered from largest to smallest pixel value`,
        });
    }
};
const ComponentTreeSchema = z
    .object({
    breakpoints: z.array(BreakpointSchema).superRefine(breakpointsRefinement),
    children: z.array(ComponentTreeNodeSchema),
    schemaVersion: SchemaVersions,
})
    .strict();
const localeWrapper = (fieldSchema) => z.record(z.string(), fieldSchema);
const ExperienceFieldsCMAShapeSchema = z.object({
    componentTree: localeWrapper(ComponentTreeSchema),
    dataSource: localeWrapper(DataSourceSchema),
    unboundValues: localeWrapper(UnboundValuesSchema),
    usedComponents: localeWrapper(UsedComponentsSchema).optional(),
    componentSettings: localeWrapper(ComponentSettingsSchema).optional(),
});

const ComponentDefinitionSchema = z.object({
    id: DefinitionPropertyKeySchema,
    variables: z.record(DefinitionPropertyKeySchema, ComponentVariableSchema.extend({
        defaultValue: PrimitiveValueSchema.optional(),
    }).superRefine((val, ctx) => {
        switch (val.type) {
            case 'Array':
                if (typeof val.defaultValue !== 'undefined') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue is not supported for "Array" type for ${ctx.path.join('.')}`,
                        fatal: false,
                    });
                }
                break;
            case 'Boolean':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'boolean') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a boolean when type is "Boolean" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Date':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'string') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a string when type is "Date" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Hyperlink':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'string') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a string when type is "Hyperlink" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Link':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'object') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue is not supported for "Link" type for ${ctx.path.join('.')}`,
                        fatal: false,
                    });
                }
                break;
            case 'Location':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'object') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be an object when type is "Location" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Media':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'string') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a string when type is "Media" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Number':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'number') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a number when type is "Number" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Object':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'object') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be an object when type is "Object" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'RichText':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'object') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be an object when type is "RichText" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Text':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'string') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a string when type is "Text" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
        }
    })),
});

var CodeNames;
(function (CodeNames) {
    CodeNames["Type"] = "type";
    CodeNames["Required"] = "required";
    CodeNames["Unexpected"] = "unexpected";
    CodeNames["Regex"] = "regex";
    CodeNames["In"] = "in";
    CodeNames["Size"] = "size";
    CodeNames["Custom"] = "custom";
})(CodeNames || (CodeNames = {}));
const convertInvalidType = (issue) => {
    const name = issue.received === 'undefined' ? CodeNames.Required : CodeNames.Type;
    const details = issue.received === 'undefined'
        ? `The property "${issue.path.slice(-1)}" is required here`
        : `The type of "${issue.path.slice(-1)}" is incorrect, expected type: ${issue.expected}`;
    return {
        details: details,
        name: name,
        path: issue.path,
        value: issue.received.toString(),
    };
};
const convertUnrecognizedKeys = (issue) => {
    const missingProperties = issue.keys.map((k) => `"${k}"`).join(', ');
    return {
        details: issue.keys.length > 1
            ? `The properties ${missingProperties} are not expected`
            : `The property ${missingProperties} is not expected`,
        name: CodeNames.Unexpected,
        path: issue.path,
    };
};
const convertInvalidString = (issue) => {
    return {
        details: issue.message || 'Invalid string',
        name: issue.validation === 'regex' ? CodeNames.Regex : CodeNames.Unexpected,
        path: issue.path,
    };
};
const convertInvalidEnumValue = (issue) => {
    return {
        details: issue.message || 'Value must be one of expected values',
        name: CodeNames.In,
        path: issue.path,
        value: issue.received.toString(),
        expected: issue.options,
    };
};
const convertInvalidLiteral = (issue) => {
    return {
        details: issue.message || 'Value must be one of expected values',
        name: CodeNames.In,
        path: issue.path,
        value: issue.received,
        expected: [issue.expected],
    };
};
const convertTooBig = (issue) => {
    return {
        details: issue.message || `Size should be at most ${issue.maximum}`,
        name: CodeNames.Size,
        path: issue.path,
        max: issue.maximum,
    };
};
const convertTooSmall = (issue) => {
    return {
        details: issue.message || `Size should be at least ${issue.minimum}`,
        name: CodeNames.Size,
        path: issue.path,
        min: issue.minimum,
    };
};
const defaultConversion = (issue) => {
    return {
        details: issue.message || 'An unexpected error occurred',
        name: CodeNames.Custom,
        path: issue.path.map(String),
    };
};
const zodToContentfulError = (issue) => {
    switch (issue.code) {
        case ZodIssueCode.invalid_type:
            return convertInvalidType(issue);
        case ZodIssueCode.unrecognized_keys:
            return convertUnrecognizedKeys(issue);
        case ZodIssueCode.invalid_enum_value:
            return convertInvalidEnumValue(issue);
        case ZodIssueCode.invalid_string:
            return convertInvalidString(issue);
        case ZodIssueCode.too_small:
            return convertTooSmall(issue);
        case ZodIssueCode.too_big:
            return convertTooBig(issue);
        case ZodIssueCode.invalid_literal:
            return convertInvalidLiteral(issue);
        default:
            return defaultConversion(issue);
    }
};

const validateBreakpointsDefinition = (breakpoints) => {
    const result = z
        .array(BreakpointSchema)
        .superRefine(breakpointsRefinement)
        .safeParse(breakpoints);
    if (!result.success) {
        return {
            success: false,
            errors: result.error.issues.map(zodToContentfulError),
        };
    }
    return { success: true };
};

const validateComponentDefinition = (definition) => {
    const result = ComponentDefinitionSchema.safeParse(definition);
    if (!result.success) {
        return {
            success: false,
            errors: result.error.issues.map(zodToContentfulError),
        };
    }
    return { success: true };
};

const VERSION_SCHEMAS = {
    '2023-09-28': ExperienceFieldsCMAShapeSchema,
};
/**
 *
 * @param experience The experience entry to validate
 * @param schemaVersionOverride Optional override for the schema version to validate against. By default the schema version is read from the experience entry
 * @returns object with success property and optional errors array
 */
const validateExperienceFields = (experience, schemaVersionOverride) => {
    let schemaVersion;
    if (experience.fields.componentTree) {
        const locale = Object.keys(experience.fields.componentTree)[0];
        schemaVersion = experience.fields.componentTree[locale].schemaVersion;
    }
    const schema = VERSION_SCHEMAS[schemaVersionOverride || schemaVersion];
    if (!schema) {
        return {
            success: false,
            errors: [
                {
                    name: schemaVersion ? CodeNames.In : CodeNames.Required,
                    expected: ['2023-09-28'],
                    value: schemaVersion,
                    path: ['fields', 'componentTree', 'schemaVersion'],
                    details: schemaVersion
                        ? 'Unsupported schema version'
                        : 'The property "schemaVersion" is required here',
                },
            ],
        };
    }
    const fieldsToValidate = {
        componentTree: experience.fields.componentTree,
        dataSource: experience.fields.dataSource,
        unboundValues: experience.fields.unboundValues,
        usedComponents: experience.fields.usedComponents,
        componentSettings: experience.fields.componentSettings,
    };
    const result = schema.safeParse(fieldsToValidate);
    if (!result.success) {
        return {
            success: result.success,
            errors: result.error.issues.map(zodToContentfulError),
        };
    }
    return { success: true };
};

export { ComponentDefinitionSchema, ExperienceFieldsCMAShapeSchema as Schema_2023_09_28, validateBreakpointsDefinition, validateComponentDefinition, validateExperienceFields };
//# sourceMappingURL=index.js.map
